import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import * as authService from '../services/authService';
import Loading from '../components/common/Loading';

const AuthContext = createContext<any>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const login = (userData: any) => {
    setUser(userData);
    saveUser(userData);
  };

  const logout = async () => {
    setUser(null);
    try {
      localStorage.removeItem('@user');
    } catch (e) {
      console.log('Logout error', e);
    }
  };

  const saveUser = async (user: any) => {
    try {
      const jsonUser = JSON.stringify(user);
      localStorage.setItem('@user', jsonUser);
    } catch (e) {
      console.log('Save user error', e);
    }
  };

  const loadUser = async () => {
    try {
      const user = await authService.refreshToken();
      return user;
    } catch (e) {
      console.log('Load user error', e);
      return null;
    }
  };

  useEffect(() => {
    const checkAuthState = async () => {
      const storedAuthState = await loadUser();
      if (storedAuthState) {
        login(storedAuthState);
      }
      setLoading(false);
    };
    checkAuthState();
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, needsOnboarding, setNeedsOnboarding }}>
      {children}
    </AuthContext.Provider>
  );
};
